/* Glossario — Página "Legendas do Sistema".
 *
 * Lista todos os termos do glossário com busca, filtro por categoria e
 * expansão para detalhes completos.
 */
import { useMemo, useState } from "react";
import { Search, BookOpen, ChevronDown, ChevronRight } from "lucide-react";
import { GLOSSARY_CATEGORIES, searchGlossary } from "@/lib/glossary";

export default function Glossario() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [expanded, setExpanded] = useState(() => new Set());

  const items = useMemo(() => searchGlossary(q, category), [q, category]);

  const counts = useMemo(() => {
    const c = { all: searchGlossary("", "all").length };
    for (const cat of GLOSSARY_CATEGORIES) {
      c[cat.id] = searchGlossary("", cat.id).length;
    }
    return c;
  }, []);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded bg-[#0F1B2D] text-[#C5A028] flex items-center justify-center">
          <BookOpen size={18} />
        </div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl text-[#0F1B2D] dark:text-slate-100">
          Legendas do Sistema
        </h1>
      </div>
      <p className="text-[14.5px] text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed">
        Glossário central de siglas, classificações metodológicas e termos do Projeto Nostradamus.
        Use a busca para localizar um termo específico ou navegue por categoria.
      </p>

      {/* Busca */}
      <div className="mt-6 relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por sigla, termo ou definição..."
          className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-md bg-white dark:bg-slate-900 dark:border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
          data-testid="glossary-search"
          aria-label="Buscar no glossário"
        />
      </div>

      {/* Filtros categoria */}
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => setCategory("all")}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
            category === "all"
              ? "bg-[#0F1B2D] text-white"
              : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
          }`}
          data-testid="glossary-cat-all"
        >
          Todos ({counts.all})
        </button>
        {GLOSSARY_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setCategory(cat.id)}
            data-testid={`glossary-cat-${cat.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
              category === cat.id
                ? "text-white"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
            style={category === cat.id ? { background: cat.color } : undefined}
          >
            {cat.label} ({counts[cat.id]})
          </button>
        ))}
      </div>

      {/* Resultados */}
      <div className="mt-6">
        {items.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            Nenhum termo encontrado para &quot;{q}&quot;.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="glossary-list">
            {items.map((t) => {
              const isOpen = expanded.has(t.key);
              const cat = GLOSSARY_CATEGORIES.find((c) => c.id === t.category);
              return (
                <li
                  key={t.key + t.title}
                  className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-900"
                  data-testid={`glossary-item-${t.key.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <button
                    onClick={() => toggle(t.key)}
                    className="w-full flex items-start gap-3 p-3.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                    aria-expanded={isOpen}
                  >
                    <span className="mt-0.5 text-slate-400 shrink-0">
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-semibold text-[#0F1B2D] dark:text-slate-100 text-[15px]">
                          {t.title}
                        </span>
                        {cat && (
                          <span
                            className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded text-white"
                            style={{ background: cat.color }}
                          >
                            {cat.label}
                          </span>
                        )}
                      </div>
                      <p className="text-[13.5px] text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">
                        {t.short}
                      </p>
                    </div>
                  </button>
                  {isOpen && t.full && t.full !== t.short && (
                    <div className="px-4 pb-4 pl-12 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800 pt-3">
                      {t.full}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
